/**
 * Cheap header parsers: they run on the first bytes of an untrusted file,
 * before the browser is asked to decode it.
 */

export interface ImageHeaderSize {
  width: number
  height: number
}

export const GIF_HEADER_BYTES = 10
export const BMP_HEADER_BYTES = 26
const ICO_DIRECTORY_BYTES = 6
const ICO_ENTRY_BYTES = 16
const ICO_MAX_LISTED_SIZES = 64
export const ICO_HEADER_BYTES = ICO_DIRECTORY_BYTES + ICO_ENTRY_BYTES * ICO_MAX_LISTED_SIZES

export async function readFileHeader(file: File, length: number): Promise<DataView> {
  return new DataView(await file.slice(0, length).arrayBuffer())
}

export function parseGifHeader(view: DataView): ImageHeaderSize | null {
  if (view.byteLength < GIF_HEADER_BYTES) return null
  const signature = String.fromCharCode(...Array.from({ length: 6 }, (_, index) => view.getUint8(index)))
  if (signature !== 'GIF87a' && signature !== 'GIF89a') return null
  return { width: view.getUint16(6, true), height: view.getUint16(8, true) }
}

export function parseBmpHeader(view: DataView): ImageHeaderSize | null {
  if (view.byteLength < BMP_HEADER_BYTES) return null
  if (view.getUint8(0) !== 0x42 || view.getUint8(1) !== 0x4d) return null
  const dibHeaderSize = view.getUint32(14, true)
  if (dibHeaderSize === 12) {
    return { width: view.getUint16(18, true), height: view.getUint16(20, true) }
  }
  if (dibHeaderSize < 40) return null
  // A negative height marks a top-down bitmap.
  return { width: Math.abs(view.getInt32(18, true)), height: Math.abs(view.getInt32(22, true)) }
}

/** Returns the sizes stored in an ICO directory, or null when the file is not an ICO. */
export function parseIcoHeader(view: DataView): ImageHeaderSize[] | null {
  if (view.byteLength < ICO_DIRECTORY_BYTES + ICO_ENTRY_BYTES) return null
  if (view.getUint16(0, true) !== 0 || view.getUint16(2, true) !== 1) return null
  const count = view.getUint16(4, true)
  if (count === 0) return null

  const sizes: ImageHeaderSize[] = []
  for (let index = 0; index < Math.min(count, ICO_MAX_LISTED_SIZES); index += 1) {
    const offset = ICO_DIRECTORY_BYTES + index * ICO_ENTRY_BYTES
    if (offset + ICO_ENTRY_BYTES > view.byteLength) break
    // Zero stands for 256 px in the one-byte ICO size fields.
    sizes.push({
      width: view.getUint8(offset) || 256,
      height: view.getUint8(offset + 1) || 256,
    })
  }
  return sizes
}
